/**
 * BatchCreateModal (S6.1, S6.2) — create a 裝櫃批次 from selected purchase orders.
 *
 * Assignment only: adding POs to a batch plans the load; no fulfillment
 * quantity moves until the seal fixes the loaded quantities at cut-off
 * (server rule — the cut-off handler is the only writer of qtyLoaded).
 *
 * S6.2 adds the Board 5 batch capture fields: mode 海運/空運/快遞, 櫃型
 * 20GP/40HQ (sea only — hidden otherwise, hide-don't-disable), 最後交期日,
 * 批號 (nullable), Sample 資訊. 工廠名稱/代碼 per line are stamped server-side
 * from the PO vendor; 箱數/箱號/材積 are captured later at cut-off.
 */

import { useEffect } from 'react';
import { App, Button, Col, DatePicker, Input, Modal, Row, Select, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { extractApiError } from '@/hooks/useSalesOrders';
import { useShippingPoOptions } from '@/hooks/useShipping';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import { CONTAINER_TYPES, SHIPMENT_MODES } from '../batchCapture';
import { useBatchCaptureMutations } from '../useBatchCapture';

const { Text } = Typography;

const batchSchema = z
  .object({
    vesselOrCarrier: z.string().min(1, 'shipping:batch.validation.vesselRequired'),
    etd: z.string().min(1, 'shipping:batch.validation.etdRequired'),
    eta: z.string().min(1, 'shipping:batch.validation.etaRequired'),
    poIds: z.array(z.string()).min(1, 'shipping:batch.validation.posRequired'),
    mode: z.enum(['sea', 'air', 'express']),
    containerType: z.enum(['20GP', '40HQ']).nullable(),
    finalDeliveryDate: z.string().nullable(),
    lotNumber: z.string().nullable(),
    sampleInfo: z.string().nullable(),
  })
  .refine((values) => !values.etd || !values.eta || values.eta >= values.etd, {
    message: 'shipping:batch.validation.etaBeforeEtd',
    path: ['eta'],
  });

type BatchFormData = z.infer<typeof batchSchema>;

const defaultFormValues: BatchFormData = {
  vesselOrCarrier: '',
  etd: '',
  eta: '',
  poIds: [],
  mode: 'sea',
  containerType: null,
  finalDeliveryDate: null,
  lotNumber: null,
  sampleInfo: null,
};

export interface BatchCreateModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful create (page refreshes its list). */
  onCreated: () => void;
}

const BatchCreateModal = ({ open, onClose, onCreated }: BatchCreateModalProps) => {
  const { t } = useTranslation(['shipping', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();

  const { purchaseOrders, isLoading: posLoading } = useShippingPoOptions();
  const { createCapturedBatch } = useBatchCaptureMutations();

  const {
    control,
    handleSubmit,
    trigger,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<BatchFormData>({
    resolver: zodResolver(batchSchema),
    defaultValues: defaultFormValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const watchedMode = useWatch({ control, name: 'mode' });

  // 櫃型 belongs to sea freight only — clear it whenever the mode leaves sea.
  useEffect(() => {
    if (watchedMode !== 'sea') {
      setValue('containerType', null);
    }
  }, [watchedMode, setValue]);

  const closeModal = () => {
    reset(defaultFormValues);
    onClose();
  };

  const onSubmit = async (values: BatchFormData) => {
    try {
      await createCapturedBatch({
        ...values,
        lotNumber: values.lotNumber?.trim() ? values.lotNumber.trim() : null,
        sampleInfo: values.sampleInfo?.trim() ? values.sampleInfo.trim() : null,
      });
      message.success(t('shipping:batch.messages.created'));
      closeModal();
      onCreated();
    } catch (error) {
      const body = extractApiError(error);
      message.error(body.message || t('common:messages.createFailed'));
    }
  };

  const fieldError = (key: keyof BatchFormData) =>
    errors[key] ? <Text style={formStyles.errorStyle}>{t(errors[key]?.message ?? '')}</Text> : null;

  return (
    <Modal
      open={open}
      title={t('shipping:batch.createModal.title')}
      onCancel={closeModal}
      width={800}
      footer={[
        <Button
          key="cancel"
          size="large"
          style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          onClick={closeModal}
        >
          {t('common:actions.cancel')}
        </Button>,
        <Button
          key="create"
          type="primary"
          size="large"
          style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          loading={isSubmitting}
          onClick={() => void handleSubmit(onSubmit)()}
        >
          {t('shipping:batch.createModal.submit')}
        </Button>,
      ]}
    >
      <div style={{ ...formStyles.fieldContainerStyle, marginBottom: '32px', padding: '20px' }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('shipping:batch.createModal.vesselLabel')}
            </Text>
            <Controller
              name="vesselOrCarrier"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={t('shipping:batch.createModal.vesselPlaceholder')}
                  size="large"
                  style={formStyles.inputStyle}
                  status={errors.vesselOrCarrier ? 'error' : undefined}
                />
              )}
            />
            {fieldError('vesselOrCarrier')}
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
          <Col span={12}>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('shipping:batch.createModal.modeLabel')}
            </Text>
            <Controller
              name="mode"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    trigger('mode');
                  }}
                  onBlur={field.onBlur}
                  options={SHIPMENT_MODES.map((mode) => ({
                    value: mode,
                    label: t(`shipping:batch.mode.${mode}`),
                  }))}
                  size="large"
                  style={formStyles.inputStyle}
                  status={errors.mode ? 'error' : undefined}
                />
              )}
            />
            {fieldError('mode')}
          </Col>
          {/* 櫃型 only exists for sea freight — hidden, not disabled, otherwise */}
          {watchedMode === 'sea' && (
            <Col span={12}>
              <Text style={formStyles.labelStyle}>
                {t('shipping:batch.createModal.containerTypeLabel')}
              </Text>
              <Controller
                name="containerType"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ?? undefined}
                    onChange={(value) => field.onChange(value ?? null)}
                    onBlur={field.onBlur}
                    allowClear
                    options={CONTAINER_TYPES.map((type) => ({
                      value: type,
                      label: t(`shipping:batch.containerType.${type}`),
                    }))}
                    placeholder={t('shipping:batch.createModal.containerTypePlaceholder')}
                    size="large"
                    style={formStyles.inputStyle}
                  />
                )}
              />
            </Col>
          )}
        </Row>
        <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
          <Col span={12}>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('shipping:batch.createModal.etdLabel')}
            </Text>
            <Controller
              name="etd"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(value) => {
                    field.onChange(value ? value.format('YYYY-MM-DD') : '');
                    trigger('etd');
                  }}
                  onBlur={field.onBlur}
                  size="large"
                  style={formStyles.inputStyle}
                  status={errors.etd ? 'error' : undefined}
                />
              )}
            />
            {fieldError('etd')}
          </Col>
          <Col span={12}>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('shipping:batch.createModal.etaLabel')}
            </Text>
            <Controller
              name="eta"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(value) => {
                    field.onChange(value ? value.format('YYYY-MM-DD') : '');
                    trigger('eta');
                  }}
                  onBlur={field.onBlur}
                  size="large"
                  style={formStyles.inputStyle}
                  status={errors.eta ? 'error' : undefined}
                />
              )}
            />
            {fieldError('eta')}
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
          <Col span={12}>
            <Text style={formStyles.labelStyle}>
              {t('shipping:batch.createModal.finalDeliveryLabel')}
            </Text>
            <Controller
              name="finalDeliveryDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(value) => field.onChange(value ? value.format('YYYY-MM-DD') : null)}
                  onBlur={field.onBlur}
                  size="large"
                  style={formStyles.inputStyle}
                />
              )}
            />
          </Col>
          <Col span={12}>
            <Text style={formStyles.labelStyle}>{t('shipping:batch.createModal.lotLabel')}</Text>
            <Controller
              name="lotNumber"
              control={control}
              render={({ field }) => (
                <Input
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                  onBlur={field.onBlur}
                  placeholder={t('shipping:batch.createModal.lotPlaceholder')}
                  size="large"
                  style={formStyles.inputStyle}
                />
              )}
            />
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
          <Col span={24}>
            <Text style={formStyles.labelStyle}>{t('shipping:batch.createModal.sampleLabel')}</Text>
            <Controller
              name="sampleInfo"
              control={control}
              render={({ field }) => (
                <Input.TextArea
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                  onBlur={field.onBlur}
                  rows={2}
                  placeholder={t('shipping:batch.createModal.samplePlaceholder')}
                  style={{ ...formStyles.inputStyle, height: 'auto' }}
                />
              )}
            />
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
          <Col span={24}>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('shipping:batch.createModal.posLabel')}
            </Text>
            <Controller
              name="poIds"
              control={control}
              render={({ field }) => (
                <Select
                  mode="multiple"
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value ?? []);
                    trigger('poIds');
                  }}
                  onBlur={field.onBlur}
                  options={purchaseOrders.map((po) => ({
                    value: po.id,
                    label: `${po.poNumber} — ${po.vendorName}`,
                  }))}
                  loading={posLoading}
                  optionFilterProp="label"
                  placeholder={t('shipping:batch.createModal.posPlaceholder')}
                  size="large"
                  style={formStyles.inputStyle}
                  status={errors.poIds ? 'error' : undefined}
                />
              )}
            />
            {fieldError('poIds')}
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default BatchCreateModal;
